import { Part } from "../skeleton.ts";
import { aliveCount, type FighterState, MAX_TOTAL_HP, type MatchState, totalHp } from "../state.ts";
import { tuning } from "../tuning.ts";
import type { SimEvent } from "./events.ts";

/** Elimination rules: head or torso at 0, or total below 25 %. */
export function checkEliminations(state: MatchState, events: SimEvent[]): void {
  for (const f of state.fighters) {
    if (!f.alive) continue;
    const head = f.hp[Part.Head] ?? 0;
    const torso = f.hp[Part.Torso] ?? 0;
    if (head <= 0 || torso <= 0 || totalHp(f) < MAX_TOTAL_HP * tuning.hp.eliminateBelowTotal) {
      f.alive = false;
      events.push({ type: "eliminated", id: f.id, by: f.lastHitBy });
    }
  }
}

/** Last one standing, or the clock. */
export function checkMatchEnd(state: MatchState, events: SimEvent[]): void {
  if (state.phase !== "fight") return;
  const alive = aliveCount(state);
  if (alive <= 1 && state.fighters.length >= 2) {
    const survivor = state.fighters.find((f) => f.alive);
    endMatch(state, events, survivor ? survivor.id : -1, "lastStanding");
    return;
  }
  if (state.clockTicks <= 0) {
    endMatch(state, events, timeoutWinner(state), "timeout");
  }
}

function endMatch(
  state: MatchState,
  events: SimEvent[],
  winner: number,
  reason: "lastStanding" | "timeout",
): void {
  state.phase = "ended";
  state.winner = winner;
  state.endReason = reason;
  events.push({ type: "matchEnd", winner, reason });
}

/** Highest remaining total HP wins a time-out; ties go to damage dealt, then lowest id. */
export function timeoutWinner(state: MatchState): number {
  let best: FighterState | null = null;
  for (const f of state.fighters) {
    if (!f.alive) continue;
    if (
      best === null ||
      totalHp(f) > totalHp(best) ||
      (totalHp(f) === totalHp(best) && f.damageDealt > best.damageDealt)
    ) {
      best = f;
    }
  }
  return best ? best.id : -1;
}
